import { useEffect, useState } from "react";
import { MessageSquarePlus, ThumbsUp, ThumbsDown, X, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* Spec §20 — pilot participants leave thumbs + optional comment on any page.
   Auto-hidden unless the user has opted in via localStorage.tp_pilot_cohort. */
const COHORTS = [
  ["individual", "Individual"],
  ["couple", "Couple"],
  ["family", "Family / Small Group"],
  ["large_group", "Large Group"],
  ["alumni", "Alumni Group (20-pax)"],
];

export function usePilotCohort() {
  const [cohort, setCohort] = useState(() => {
    try { return localStorage.getItem("tp_pilot_cohort") || ""; } catch { return ""; }
  });
  const save = (c) => {
    try { localStorage.setItem("tp_pilot_cohort", c || ""); } catch {}
    setCohort(c);
  };
  return [cohort, save];
}

export default function PilotFeedbackWidget({ inline = false }) {
  const [cohort, setCohort] = usePilotCohort();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setRating(null);
  }, [open]);

  // Hide the floating trigger when not opted in (no cohort selected)
  if (!inline && !cohort) return null;

  const submit = async () => {
    if (!rating) { toast.error("Pick 👍 or 👎"); return; }
    setBusy(true);
    try {
      await api.post("/pilot/feedback", {
        rating, text: text.trim() || undefined, cohort: cohort || undefined,
        page: window.location.pathname,
      });
      toast.success("Thanks — feedback captured");
      setOpen(false); setText(""); setRating(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setBusy(false); }
  };

  return (
    <>
      {!inline && (
        <button
          onClick={() => setOpen(true)}
          data-testid="pilot-feedback-fab"
          title="Share pilot feedback"
          className="fixed bottom-6 right-6 z-40 rounded-full bg-fuchsia-600 text-white shadow-lg hover:bg-fuchsia-700 w-12 h-12 flex items-center justify-center">
          <MessageSquarePlus className="w-5 h-5" />
        </button>
      )}
      {inline && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="pilot-feedback-inline">
          <div className="text-[10px] font-800 uppercase tracking-widest text-slate-500 mb-3">Pilot cohort · self-select</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {COHORTS.map(([k, l]) => (
              <button
                key={k}
                onClick={() => setCohort(k)}
                data-testid={`pilot-cohort-${k}`}
                className={`text-xs font-800 px-3 py-1.5 rounded-full border transition-colors ${cohort === k
                  ? "bg-fuchsia-600 text-white border-fuchsia-600"
                  : "bg-white text-slate-700 border-slate-300 hover:border-fuchsia-400"}`}>
                {l}
              </button>
            ))}
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid="pilot-feedback-open"
            className="inline-flex items-center gap-1.5 bg-fuchsia-600 text-white text-sm font-800 px-4 py-2.5 hover:bg-fuchsia-700">
            <MessageSquarePlus className="w-4 h-4" /> Share feedback
          </button>
          <p className="text-xs text-slate-500 mt-2">
            Your feedback goes to the Ops dashboard. We tag each entry with the cohort you pick and the page you were on.
          </p>
        </div>
      )}

      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[90] bg-black/50 flex items-end sm:items-center justify-center p-3"
          data-testid="pilot-feedback-dialog">
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div>
                <div className="text-[10px] font-800 uppercase tracking-widest text-fuchsia-600">Pilot feedback</div>
                <div className="font-serif font-800 text-lg text-slate-900">How's this page?</div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="pilot-feedback-close"
                className="text-slate-400 hover:text-slate-700 p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              {!cohort && (
                <div className="mb-4">
                  <div className="text-[10px] font-800 uppercase tracking-widest text-slate-500 mb-2">Which cohort are you in?</div>
                  <div className="flex flex-wrap gap-2">
                    {COHORTS.map(([k, l]) => (
                      <button key={k} onClick={() => setCohort(k)}
                        data-testid={`pilot-feedback-cohort-${k}`}
                        className="text-xs font-800 px-2.5 py-1 rounded-full border border-slate-300 bg-white hover:border-fuchsia-400">
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 mb-4">
                <button onClick={() => setRating("up")}
                  data-testid="pilot-feedback-thumbs-up"
                  className={`flex-1 rounded-xl border-2 py-3 flex items-center justify-center gap-2 font-800 transition-colors ${rating === "up"
                    ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 text-slate-600 hover:border-emerald-400"}`}>
                  <ThumbsUp className="w-5 h-5" /> Loved it
                </button>
                <button onClick={() => setRating("down")}
                  data-testid="pilot-feedback-thumbs-down"
                  className={`flex-1 rounded-xl border-2 py-3 flex items-center justify-center gap-2 font-800 transition-colors ${rating === "down"
                    ? "border-rose-500 bg-rose-50 text-rose-700"
                    : "border-slate-200 text-slate-600 hover:border-rose-400"}`}>
                  <ThumbsDown className="w-5 h-5" /> Needs work
                </button>
              </div>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={2000}
                rows={4}
                placeholder="What was confusing or delightful? (optional)"
                data-testid="pilot-feedback-text"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-fuchsia-500"
              />
              <div className="mt-4 flex items-center justify-between">
                <span className="text-[11px] text-slate-500 font-mono truncate max-w-[55%]">
                  {window.location.pathname}
                </span>
                <button
                  onClick={submit}
                  disabled={busy || !rating}
                  data-testid="pilot-feedback-submit"
                  className="inline-flex items-center gap-1.5 bg-fuchsia-600 text-white font-800 px-4 py-2.5 text-sm hover:bg-fuchsia-700 disabled:opacity-40">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
